import { HttpClient, HttpErrorResponse, HttpEventType } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";

/**
 * PhotosApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/photosApi/photosApi.service.js.
 *
 * `upload()` posts to a hardcoded `/v1/api/photos/1` path (legacy quirk —
 * the trailing `/1` is preserved verbatim). Angular's HttpClient handles
 * FormData boundary headers automatically, so we don't set Content-Type.
 */
const PHOTOS_BASE = "/v1/api/photos";

@Injectable({ providedIn: "root" })
export class PhotosApiService {
  private readonly http = inject(HttpClient);

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${PHOTOS_BASE}/${id}`));
  }

  /**
   * `onProgress`, when given, is called with 0-100 as the upload streams —
   * same progress feedback as the legacy jQuery-fileupload directive
   * (`fileuploadprogress` event) used elsewhere in this app (event
   * templates). Can't use `wrapAjs`/`firstValueFrom` here since those
   * resolve on the FIRST emission, which would be a progress event rather
   * than the final response — this subscribes manually and resolves only on
   * the terminal HttpEventType.Response, replicating wrapAjs's own
   * HttpErrorResponse normalization for the reject path.
   */
  upload(props: { files: File[]; onProgress?: (percent: number) => void }): Promise<AjsResponse<unknown>> {
    const data = new FormData();
    for (const file of props.files) {
      /**
       * Field name must be "files" (not "file") — the Rails proxy's
       * post_multipart action reads params[:files].tempfile (see
       * apps/b2b-backend/app/controllers/proxy_controller.rb), matching the
       * legacy jQuery-fileupload directive's `name="files"` input. A mismatch
       * here makes params[:files] nil in Rails, which raises before the
       * request ever reaches the Java backend and surfaces as an opaque 500.
       */
      data.append("files", file);
    }
    return new Promise((resolve, reject) => {
      let responded = false;
      this.http.post<unknown>(`${PHOTOS_BASE}/1`, data, { reportProgress: true, observe: "events" }).subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress) {
            const percent = event.total ? Math.round((100 * event.loaded) / event.total) : 0;
            props.onProgress?.(percent);
          } else if (event.type === HttpEventType.Response) {
            responded = true;
            resolve({ data: event.body });
          }
        },
        error: (err) => {
          if (err instanceof HttpErrorResponse) {
            reject({ data: err.error, status: err.status });
          } else {
            reject(err);
          }
        },
        // If the observable completes without ever emitting a Response
        // (e.g. cancelled by an interceptor), resolve() above never ran —
        // reject so the awaiting caller doesn't hang forever. A no-op once
        // resolve() already settled the promise in the normal path.
        complete: () => {
          if (!responded) {
            reject(new Error("Upload completed without a response."));
          }
        },
      });
    });
  }
}
